import { useAdminContext } from '@/contexts/AdminContext';
import { getUserAvatarUrl, useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@client-app/i18n';
import {
    ArrowUpRight,
    Gear, House, SignIn, SignOut
} from '@phosphor-icons/react';
import clsx from 'clsx';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { NavKey } from '../Settings/SettingsModal';

interface UserMenuProps {
  collapsed: boolean;
  showHomepage?: boolean;
  showSettings?: boolean;
  hideEmail?: boolean;
  rightSlot?: React.ReactNode;
  onOpenSettings?: () => void;
  onLogout?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
    collapsed,
    showHomepage = true,
    showSettings = true,
    hideEmail = false,
    rightSlot,
    onOpenSettings,
    onLogout,
}) => {
    const { t } = useTranslation();
    const {
        user, logout, openSignInModal 
    } = useAuth();
    const { modal } = useAdminContext();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuPanelRef = useRef<HTMLDivElement>(null);
    const [menuLayout, setMenuLayout] = useState<{ top?: number; bottom?: number; left: number; width: number }>({
        bottom: 0,
        left: 0,
        width: 224,
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (menuRef.current?.contains(target) || menuPanelRef.current?.contains(target)) return;
            setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const computeMenuLayout = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const viewportPadding = 12;
        const gap = 8;
        const width = collapsed ? 224 : Math.max(224, rect.width);
        const maxLeft = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);

        let left = collapsed ? rect.right + gap : rect.left;
        left = Math.min(Math.max(viewportPadding, left), maxLeft);

        if (collapsed) {
            setMenuLayout({
                bottom: Math.max(viewportPadding, window.innerHeight - rect.bottom),
                left,
                width,
            });
            return;
        }

        const estimatedHeight = 112;
        const shouldOpenBelow =
            rect.top - gap - estimatedHeight < viewportPadding
            && window.innerHeight - rect.bottom - gap >= estimatedHeight;

        setMenuLayout(
            shouldOpenBelow
                ? {
                    top: rect.bottom + gap,
                    left,
                    width,
                }
                : {
                    bottom: Math.max(viewportPadding, window.innerHeight - rect.top + gap),
                    left,
                    width,
                },
        );
    }, [collapsed]);

    useEffect(() => {
        if (!isOpen) return;
        computeMenuLayout();
        const handleResize = () => computeMenuLayout();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [computeMenuLayout, isOpen]);

    // 未登录状态显示 Login 按钮
    if (!user) {
        return (
            <div
                className={clsx(
                    "shrink-0 py-2 border-t border-gray-100/50 z-20",
                    collapsed ? "px-2 flex flex-col items-center" : "px-4"
                )}
            >
                <button
                    onClick={() => openSignInModal()}
                    className="flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors w-full"
                >
                    <SignIn size={18} weight="bold" />
                    <span>{t('website.userMenu.login')}</span>
                </button>
            </div>
        );
    }

    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || t('website.userMenu.user');
    const userEmail = user?.email;
    const avatarUrl = getUserAvatarUrl(user);
    const userTrigger = (
        <button
            ref={triggerRef}
            type="button"
            aria-label={userName}
            title={collapsed ? userName : undefined}
            className={clsx(
                "relative flex min-w-0 items-center gap-2.5 rounded-xl p-1.5 text-left transition-colors hover:bg-gray-200 outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
                isOpen && "bg-gray-200",
                collapsed ? "justify-center" : "flex-1"
            )}
            onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) {
                    computeMenuLayout();
                }
                setIsOpen(!isOpen);
            }}
        >
            <img
                src={avatarUrl}
                alt={userName}
                className="w-8 h-8 shrink-0 rounded-full border border-gray-200 bg-white"
            />
            {!collapsed && (
                <div className="flex-1 min-w-0 text-left">
                    <p className="truncate text-[15px] font-medium text-text-main text-gray-900">{userName}</p>
                    {!hideEmail && userEmail ? (
                        <p className="text-xs text-text-sub truncate text-gray-500">{userEmail}</p>
                    ) : null}
                </div>
            )}
        </button>
    );

    return (
        <div
            ref={menuRef}
            className={clsx(
                "shrink-0 py-2 border-t border-gray-100/50 z-20 relative",
                collapsed ? "px-2 flex flex-col items-center" : "px-4"
            )}
        >
            {/* Settings & Logout - Popup Menu */}
            {isOpen ? createPortal(
                <div
                    ref={menuPanelRef}
                    className="fixed z-[9999] animate-in fade-in zoom-in-95 duration-200"
                    style={{
                        top: menuLayout.top,
                        bottom: menuLayout.bottom,
                        left: menuLayout.left,
                        width: menuLayout.width
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 space-y-0.5">
                        {showSettings ? (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    if (onOpenSettings) {
                                        onOpenSettings();
                                    } else {
                                        modal.openModal('profile', { activeTab: NavKey.profile });
                                    }
                                }}
                                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
                            >
                                <Gear size={16} weight="bold" className="text-gray-500" />
                                {t('common.settings')}
                            </button>
                        ) : null}
                        {showHomepage ? (
                            <button
                                onClick={() => { window.open('/?index=1', '_blank'); setIsOpen(false); }}
                                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
                            >
                                <House size={16} weight="bold" className="text-gray-500" />
                                <span className="flex-1">{t('website.userMenu.homepage')}</span>
                                <ArrowUpRight size={16} weight="bold" className="text-gray-400" />
                            </button>
                        ) : null}
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (onLogout) {
                                    onLogout();
                                } else {
                                    logout();
                                }
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                        >
                            <SignOut size={16} weight="bold" className="shrink-0" />
                            <span className="truncate">{t('common.signOut')}</span>
                        </button>
                    </div>
                </div>,
                document.body
            ) : null}

            <div className={clsx("flex w-full items-center", collapsed ? "justify-center" : "gap-1.5")}>
                {userTrigger}
                {!collapsed && rightSlot ? (
                    <div
                        className="shrink-0"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                    >
                        {rightSlot}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default UserMenu;
